/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, computed, ObservableMap, toJS } from 'mobx';
import {
    generateDetail,
    generateList,
    generateCreate,
    generateUpdate,
    generateListMore,
    generateDelete,
} from './actionCreator';
import forEach from 'lodash/forEach';
import _ from '../translations';

class ContentsStore {
    /*this collection will save a flatten version of the contents tree, where the key is the parent id, the root level will contain*/
    @observable collection = new ObservableMap();
    @observable details = new ObservableMap();

    constructor(isLive = false) {
        this.baseUrl = '/api/ott/contents';
        this.isLive = isLive;
    }

    @action
    reset = () => {
        this.collection.clear();
        this.details.clear();
    };

    @action.bound
    async detail({ _id = false } = {}) {
        return await generateDetail({
            _id,
            url: `${this.baseUrl}/${_id}`,
            store: this,
            customError: _('There was a problem fetching the content'),
        })();
    }

    @action.bound
    async contentDetail({ _id = false } = {}) {
        return await generateDetail({
            _id,
            url: `${this.baseUrl}/contentDetails/${_id}`,
            store: this,
            customError: _('There was a problem fetching the content'),
        })();
    }

    @action.bound
    async list({ parent_id = 'root', params = {}, hash, deactivateLiveFilter = false } = {}) {
        if (parent_id !== 'root' && parent_id !== 'any') params['filterBy-__belong.Content._id'] = parent_id;
        else if (!parent_id || parent_id === 'root') params.isNull = '__belong.Content';

        if (!deactivateLiveFilter) params['filterBy-live'] = String(this.isLive);

        return await generateList({
            hash: hash ? hash : parent_id,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing contents'),
        })();
    }

    @action.bound
    async listMore({ hash, params = {}, deactivateLiveFilter = false } = {}) {
        if (!deactivateLiveFilter) params['filterBy-live'] = String(this.isLive);

        return await generateListMore({
            hash,
            params,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem listing contents'),
        })();
    }

    @action.bound
    async create({ item = {} } = {}) {
        return await generateCreate({ item, url: this.baseUrl, store: this })();
    }

    @action.bound
    async update({ items, item = {} } = {}) {
        return await generateUpdate({
            items,
            item,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem updating the content'),
        })();
    }

    @action.bound
    async delete({ items, _id = false, type = 'logical' } = {}) {
        return await generateDelete({
            items,
            _id,
            type,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem deleting the content'),
        })();
    }

    /*This function will be called any time that stores changes, generating a new contents tree*/
    @computed
    get tree() {
        //get root contents
        if (!this.collection.has('root'))
            return {
                items: [],
            };

        let root = toJS(this.collection.get('root'));

        const fillChildrens = node => {
            if (!node.items) return;
            forEach(node.items, item => {
                if (!this.collection.has(item._id)) return;
                const list = this.collection.get(item._id);
                const items = list.items ? list.items.toJS() : [];
                const pagination = list.pagination;
                item.children = {
                    items,
                    pagination,
                };
                fillChildrens(toJS(this.collection.get(item._id)));
            });
        };
        fillChildrens(root);
        return root;
    }
}

const vodStore = new ContentsStore(false);
const liveStore = new ContentsStore(true);

export { vodStore, liveStore };
